import useSWR, { SWRConfiguration } from 'swr';
import axios, { AxiosRequestConfig } from 'axios';
import { applyTraceHeaders } from '../functions/observability/traceHeaders';
import { ATTIK_REQUEST_ID_HEADER } from '@/util/observability/requestId';
import { ATTIK_CACHE_HEADER } from '@/util/observability/dashboardPerf/constants';
import { isDashboardPerfUrl } from '@/util/observability/dashboardPerf/urls';
import { useDashboardPerf } from '@/util/observability/dashboardPerf/DashboardPerfProvider';

type ArgsProps = {
  url: string;
  method?: 'get';
  body?: any;
  headers?: any;
  params?: any;
};

// const fetcher = async (config: AxiosRequestConfig): Promise<T> => {
//   // console.log('config', config);

//   return await axios({
//     url: `/api/server/${config.url}`,
//     method: 'post',
//     data: config,
//   });
// };

type Options = Parameters<typeof useSWR>[1];

export default function useServer<T>(
  args: ArgsProps | null,
  options?: SWRConfiguration
) {
  const { url, method = 'get', body, headers, params } = args || {};
  const dashboardPerf = useDashboardPerf();
  const compiledKey = {
    // url: `http://localhost:6000/${url}}`,
    url: url,
    method,
    data: body,
    headers,
    params,
  };

  // Removing the data call from the server and moving it to the client due to untracable errors.
  // const fetcher = async (config: any): Promise<T> => {
  //   return await callServerAction(config);
  // };

  const fetcher = async (config: AxiosRequestConfig): Promise<T> => {
    const traceHeaders = applyTraceHeaders({
      'Content-Type': 'application/json',
      ...(config.headers as Record<string, string | undefined>),
    });
    const requestId = traceHeaders[ATTIK_REQUEST_ID_HEADER];
    const trackPerf = Boolean(config.url && isDashboardPerfUrl(String(config.url)));
    const startedAt = trackPerf ? performance.now() : 0;

    if (trackPerf && dashboardPerf) {
      dashboardPerf.registerRequestStart();
    }

    let perfRecorded = false;

    try {
      const response = await axios({
        url: `/api/server/${config.url}`,
        method: 'post',
        data: config,
        headers: traceHeaders as Record<string, string>,
      });

      const failed =
        response.data &&
        typeof response.data === 'object' &&
        response.data.success === false;

      if (trackPerf && dashboardPerf) {
        perfRecorded = true;
        const cacheHit =
          (response.headers[ATTIK_CACHE_HEADER] as string | undefined)?.toUpperCase() ??
          null;
        dashboardPerf.registerRequestEnd(String(config.url), {
          durationMs: performance.now() - startedAt,
          cacheHit,
          failed: Boolean(failed),
          requestId,
        });
      }

      if (failed) {
        const error = new Error(response.data.error || 'Server request failed');
        (error as any).status = response.data.status;
        (error as any).code = response.data.code;
        throw error;
      }

      return response.data;
    } catch (error) {
      if (trackPerf && dashboardPerf && !perfRecorded) {
        dashboardPerf.registerRequestEnd(String(config.url), {
          durationMs: performance.now() - startedAt,
          cacheHit: null,
          failed: true,
          requestId,
        });
      }
      throw error;
    }
  };

  const { data, error, isLoading, isValidating, mutate } = useSWR<T>(
    args ? compiledKey : null,
    fetcher,
    options
  );

  // console.log(error, data);
  // if (error) throw error;

  return {
    data: data,
    loading: isLoading,
    validating: isValidating,
    error,
    mutate,
  };
}

export type ServerResponse = {
  data: any;
  error: any;
  loading: boolean;
  validating: boolean;
  mutate: (data?: any, shouldRevalidate?: boolean) => Promise<any>;
};
